import type { ReactNode } from "react"
import {
	IconBookOpen4Outline18 as BookOpen,
	IconCubeOutline18 as Box,
	IconDarkModeOutline18 as DarkMode,
	IconLanguageOutline18 as Language,
	IconStackPerspectiveOutline18 as StackPerspective,
	IconMessagesOutline18 as Messages,
	IconMoonOutline18 as Moon,
	IconSunOutline18 as Sun,
} from "nucleo-ui-outline-18"

import { Button, IconButton, ProductlaneIcon } from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

const DIVIDER = "border-t border-gray-300 dark:border-gray-700/80"
const FOOTER_ROW = "flex items-center gap-2 p-2"
const LINK =
	"text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"

const ThemeCircle = () => (
	<IconButton
		variant="glass"
		isRounded
		Icon={Sun}
		aria-label="Light"
		className="h-7 w-7"
		iconClassName="h-[0.85rem] w-[0.85rem] text-gray-500 dark:text-gray-400"
	/>
)

// 0. What ships today: one circle, right-aligned, nothing to balance it.
export function FooterToday() {
	return (
		<div className={`${FOOTER_ROW} justify-end`}>
			<ThemeCircle />
		</div>
	)
}

// 1. Three segments across the full width, so the row has no gap to explain.
export function FooterSegmentedTheme() {
	const segments: [string, typeof Sun][] = [
		["Light", Sun],
		["Dark", Moon],
		["Auto", DarkMode],
	]
	return (
		<div className="p-2">
			<div className="flex rounded-full bg-gray-100 p-0.5 dark:bg-gray-850">
				{segments.map(([label, Icon], index) => (
					<button
						key={label}
						type="button"
						aria-label={label}
						className={`flex h-6 flex-1 items-center justify-center rounded-full ${
							index === 0
								? "bg-white text-gray-900 shadow-sm ring-1 ring-black/5 dark:bg-gray-750 dark:text-gray-100 dark:ring-white/10"
								: "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
						}`}
					>
						<Icon className="h-[0.85rem] w-[0.85rem] flex-none" />
					</button>
				))}
			</div>
		</div>
	)
}

// 2. The deflection CTA every docs site puts under its nav.
export function FooterAskCta() {
	return (
		<div className={`${FOOTER_ROW} justify-between`}>
			<Button variant="ghost" size="sm" Icon={Messages} className="!px-2">
				Ask us
			</Button>
			<ThemeCircle />
		</div>
	)
}

// 3. The two "how I read this" settings, one at each end.
export function FooterLanguageAndTheme() {
	return (
		<div className={`${FOOTER_ROW} justify-between`}>
			<Button variant="ghost" size="sm" Icon={Language} className="!px-2">
				English
			</Button>
			<ThemeCircle />
		</div>
	)
}

// 4. Docs is a dead end today; these are the other surfaces they own.
export function FooterSurfaceLinks() {
	const links: [string, typeof Sun][] = [
		["Changelog", StackPerspective],
		["Roadmap", Box],
		["Support", BookOpen],
	]
	return (
		<div className={`${FOOTER_ROW} justify-between`}>
			<div className="flex flex-col gap-1 pl-1">
				{links.map(([label, Icon]) => (
					<button
						key={label}
						type="button"
						className={`flex items-center gap-2 ${LINK}`}
					>
						<Icon className="h-3.5 w-3.5 flex-none" />
						{label}
					</button>
				))}
			</div>
			<ThemeCircle />
		</div>
	)
}

// 5. The classic footer shape, hidden once a workspace pays for branding off.
export function FooterPoweredBy() {
	return (
		<div className={`${FOOTER_ROW} justify-between`}>
			<button
				type="button"
				className={`flex items-center gap-1.5 pl-1 ${LINK}`}
			>
				<ProductlaneIcon className="h-3.5 w-3.5 flex-none" />
				Powered by Productlane
			</button>
			<ThemeCircle />
		</div>
	)
}

const NAV_GROUPS: [string, string[]][] = [
	["Getting started", ["Install the widget", "Invite your team"]],
	["Inbox", ["Assign a thread", "Snooze and reopen", "Saved replies"]],
	["AI agent", ["Train on your docs", "Handoff rules"]],
]

function SidebarPreview({ children }: { children: ReactNode }) {
	return (
		<div className="flex h-[420px] w-[280px] flex-none flex-col overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-700/80 dark:bg-gray-900">
			<div className="flex-1 overflow-hidden px-4 pt-4">
				{NAV_GROUPS.map(([group, rows]) => (
					<div key={group} className="pb-3">
						<div className="pb-1 pl-2 text-xs font-medium text-primary">
							{group}
						</div>
						{rows.map((row) => (
							<div
								key={row}
								className="rounded-md px-2 py-1 text-xs text-gray-500 dark:text-gray-400"
							>
								{row}
							</div>
						))}
					</div>
				))}
			</div>
			<div className={DIVIDER} />
			{children}
		</div>
	)
}

export function PortalSidebarFooterPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1180px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Portal sidebar footer, five options
					</div>
					<div className="mt-1 text-sm text-secondary">
						The bar under the docs, requests, and support sidebars. Today it
						carries one theme circle, and it disappears entirely when a
						workspace pins a theme.
					</div>
				</div>
				<div className="flex flex-wrap gap-8">
					<Variant
						label="Today"
						note="One 28px circle against the right edge, nothing on the left."
					>
						<FooterToday />
					</Variant>
					<Variant
						label="1. Segmented theme"
						note="Light, dark, auto across the full width. The state reads without a hover, and the row has no empty half."
					>
						<FooterSegmentedTheme />
					</Variant>
					<Variant
						label="2. Ask us"
						note="Opens the widget modal the support CTA already uses. Weight on the left, theme on the right, and it feeds threads."
					>
						<FooterAskCta />
					</Variant>
					<Variant
						label="3. Language and theme"
						note="47 locales ship today and the picker is buried. Two controls at opposite ends read as a real footer bar."
					>
						<FooterLanguageAndTheme />
					</Variant>
					<Variant
						label="4. Other surfaces"
						note="Changelog, roadmap, support. Gives the column a base and a way out of the docs."
					>
						<FooterSurfaceLinks />
					</Variant>
					<Variant
						label="5. Powered by Productlane"
						note="Cheapest, and the shape people expect. Gated like portal-footer.tsx, so paying workspaces are back to a lone circle."
					>
						<FooterPoweredBy />
					</Variant>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="w-[280px] space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="min-h-[48px] text-xxs text-tertiary">{note}</div>
			<SidebarPreview>{children}</SidebarPreview>
		</div>
	)
}
